import { Box, Group, Stack, UnstyledButton, createStyles } from '@mantine/core';
import type { VersionHistoryOut } from '@repo/api-codegen';
import { Icon, Text } from '@repo/foundations';
import dayjs from 'dayjs';
import { uniqBy } from 'lodash-es';
import { useMemo } from 'react';
import { useSuspenseFeatureAccess } from '../../../api/hooks/workspace/useSuspenseFeatureAccess';
import type { IActivityLogPerformer } from '../../ActivityLogPerformer/types';
import { VersionHistoryButtonItem } from './VersionHistoryButtonItem';

const useStyles = createStyles((theme) => ({
	button: {
		width: '100%',
		backgroundColor: theme.other.getColor('surface/primary/default'),
		padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
		textAlign: 'left',
		borderRadius: theme.radius.sm,
		alignItems: 'center',

		'&:hover, &:focus': {
			backgroundColor: theme.other.getColor('surface/primary/hover'),
			textDecoration: 'none',
			color: theme.other.getColor('text/primary/default'),
		},

		'&:active, &[data-active="true"]': {
			backgroundColor: theme.other.getColor('surface/primary/active'),
		},
	},
	root: {
		justifyContent: 'space-between',
		display: 'flex',
	},
	group: {
		gap: 0,
		padding: 0,
	},
	iconGroup: {
		alignItems: 'center',
	},
}));

export interface VersionHistoryButtonProps {
	versionHistory: VersionHistoryOut;
	selected?: boolean;
	onClick: () => void;
}

export function VersionHistoryButton({
	versionHistory,
	selected,
	onClick,
}: VersionHistoryButtonProps) {
	const { classes } = useStyles();
	const { oldVersionHistoryAccess } = useSuspenseFeatureAccess();

	const performers = useMemo(
		() =>
			uniqBy(
				versionHistory.activity_logs.filter(
					(log) => !!log.performer_metadata || log.performer_type === 'UNKNOWN'
				),
				(log) => log.performer_metadata?.id ?? 'unknown'
			).map(
				(log) =>
					({
						id: log.performer_metadata?.id ?? 'unknown',
						type: log.performer_type,
						metadata: log.performer_metadata!,
					}) as IActivityLogPerformer
			),
		[versionHistory.activity_logs]
	);

	const isOlderThan30Days = !!versionHistory.older_than_thirty_days;
	const displayLockIcon = isOlderThan30Days && !oldVersionHistoryAccess;

	return (
		<UnstyledButton
			key={versionHistory.id}
			onClick={onClick}
			className={classes.button}
			data-active={selected ? 'true' : undefined}
		>
			<Box className={classes.root}>
				<Group className={classes.group}>
					<Text size="sm" weight="bold">
						{dayjs(versionHistory.latest_activity_logged_at).format(
							'MMM D [at] h:mm A'
						)}
					</Text>
					<Stack spacing="xs" py="3xs">
						{performers.map((performer) => (
							<VersionHistoryButtonItem
								key={performer.id}
								performer={performer}
							/>
						))}
					</Stack>
				</Group>
				{displayLockIcon && (
					<Group className={classes.iconGroup}>
						<Icon name="lock" size="md" color="icon/secondary/default" />
					</Group>
				)}
			</Box>
		</UnstyledButton>
	);
}
